// frontend/src/components/public/blocks/LinksBlock.tsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  MusicNote as TikTokIcon,
  Album as SpotifyIcon,
  LibraryMusic as BandcampIcon,
  Cloud as SoundCloudIcon,
  ConfirmationNumber as TicketIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Description as DocumentIcon,
  Handshake as PartnershipIcon,
  Article as PressIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

interface LinksBlockProps {
  data: {
    heading: string;
    description?: string;
    layout: 'compact' | 'cards' | 'hybrid';
    socialLinks: Array<{
      platform: string;
      url: string;
      handle?: string;
      followerCount?: string;
      customIcon?: string;
      customLabel?: string;
    }>;
    resourceLinks: Array<{
      title: string;
      description?: string;
      url: string;
      icon: string;
      customIcon?: string;
      ctaText: string;
      openInNewTab: boolean;
      backgroundColor?: string;
    }>;
    showContactCta: boolean;
    contactCtaHeading?: string;
    contactCtaDescription?: string;
    contactCtaButtonText?: string;
    contactCtaButtonLink?: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const LinksBlock: React.FC<LinksBlockProps> = ({ data, theme }) => {
  const {
    heading,
    description,
    layout = 'hybrid',
    socialLinks = [],
    resourceLinks = [],
    showContactCta = false,
    contactCtaHeading,
    contactCtaDescription,
    contactCtaButtonText,
    contactCtaButtonLink
  } = data;

  const primaryColor = theme?.primaryColor || '#2C3E50';
  const accentColor = theme?.accentColor || '#3A9188';

  // Social platform icons and colors
  const getSocialIcon = (platform: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      instagram: <InstagramIcon sx={{ fontSize: 32 }} />,
      facebook: <FacebookIcon sx={{ fontSize: 32 }} />,
      youtube: <YouTubeIcon sx={{ fontSize: 32 }} />,
      twitter: <TwitterIcon sx={{ fontSize: 32 }} />,
      linkedin: <LinkedInIcon sx={{ fontSize: 32 }} />,
      tiktok: <TikTokIcon sx={{ fontSize: 32 }} />,
      spotify: <SpotifyIcon sx={{ fontSize: 32 }} />,
      bandcamp: <BandcampIcon sx={{ fontSize: 32 }} />,
      soundcloud: <SoundCloudIcon sx={{ fontSize: 32 }} />,
      custom: <LinkIcon sx={{ fontSize: 32 }} />
    };
    return iconMap[platform] || <LinkIcon sx={{ fontSize: 32 }} />;
  };

  const getSocialColor = (platform: string) => {
    const colorMap: Record<string, string> = {
      instagram: '#E4405F',
      facebook: '#1877F2',
      youtube: '#FF0000',
      twitter: '#1DA1F2',
      linkedin: '#0A66C2',
      tiktok: '#000000',
      spotify: '#1DB954',
      bandcamp: '#629AA9',
      soundcloud: '#FF5500',
      custom: accentColor
    };
    return colorMap[platform] || accentColor;
  };

  const getPlatformName = (platform: string, customLabel?: string) => {
    if (platform === 'custom' && customLabel) return customLabel;
    const nameMap: Record<string, string> = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      youtube: 'YouTube',
      twitter: 'X (formerly Twitter)',
      linkedin: 'LinkedIn',
      tiktok: 'TikTok',
      spotify: 'Spotify',
      bandcamp: 'Bandcamp',
      soundcloud: 'SoundCloud'
    };
    return nameMap[platform] || platform;
  };

  // Resource link icons
  const getResourceIcon = (icon: string, customIcon?: string) => {
    if (icon === 'custom' && customIcon) {
      return <img src={customIcon} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />;
    }

    const iconMap: Record<string, React.ReactElement> = {
      ticket: <TicketIcon sx={{ fontSize: 48 }} />,
      download: <DownloadIcon sx={{ fontSize: 48 }} />,
      email: <EmailIcon sx={{ fontSize: 48 }} />,
      phone: <PhoneIcon sx={{ fontSize: 48 }} />,
      calendar: <CalendarIcon sx={{ fontSize: 48 }} />,
      document: <DocumentIcon sx={{ fontSize: 48 }} />,
      partnership: <PartnershipIcon sx={{ fontSize: 48 }} />,
      press: <PressIcon sx={{ fontSize: 48 }} />
    };
    return iconMap[icon] || <LinkIcon sx={{ fontSize: 48 }} />;
  };

  // Social Links Component
  const SocialLinksSection = ({ showLabels = false }: { showLabels?: boolean }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: showLabels ? 3 : 2,
        mb: layout === 'hybrid' ? 6 : 0
      }}
    >
      {socialLinks.map((social, index) => {
        const platformColor = getSocialColor(social.platform);
        const icon = social.platform === 'custom' && social.customIcon
          ? <img src={social.customIcon} alt={social.customLabel} style={{ width: 32, height: 32 }} />
          : getSocialIcon(social.platform);

        if (showLabels) {
          // Card style with labels
          return (
            <Box
             id="links"
              key={index}
              component="a"
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'none',
                textAlign: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: platformColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  mb: 1,
                  boxShadow: `0 4px 12px ${platformColor}44`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: `0 8px 24px ${platformColor}66`,
                    transform: 'scale(1.05)'
                  }
                }}
              >
                {icon}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: primaryColor,
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                {getPlatformName(social.platform, social.customLabel)}
              </Typography>
              {social.handle && (
                <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                  {social.handle}
                </Typography>
              )}
              {social.followerCount && (
                <Typography variant="caption" sx={{ color: accentColor, fontWeight: 600, display: 'block' }}>
                  {social.followerCount}
                </Typography>
              )}
            </Box>
          );
        }

        // Compact icon-only style
        return (
          <Tooltip
            key={index}
            title={`${getPlatformName(social.platform, social.customLabel)}${social.followerCount ? ` - ${social.followerCount}` : ''}`}
            arrow
          >
            <IconButton
              component="a"
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: platformColor,
                color: '#fff',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: platformColor,
                  transform: 'scale(1.1)',
                  boxShadow: `0 8px 16px ${platformColor}66`
                },
                transition: 'all 0.3s'
              }}
            >
              {icon}
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );

  // Resource Links Component
  const ResourceLinksSection = () => (
    <Grid container spacing={3}>
      {resourceLinks.map((resource, index) => (
        <Grid item xs={12} sm={6} md={resourceLinks.length === 2 ? 6 : 4} key={index}>
          <Card
            sx={{
              height: '100%',
              bgcolor: resource.backgroundColor || '#fff',
              borderRadius: 2,
              border: '2px solid #e0e0e0',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                borderColor: accentColor,
                boxShadow: `0 12px 32px ${accentColor}33`
              }
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              {/* Icon */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  bgcolor: `${accentColor}22`,
                  color: accentColor,
                  mb: 3
                }}
              >
                {getResourceIcon(resource.icon, resource.customIcon)}
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: primaryColor,
                  mb: resource.description ? 2 : 3
                }}
              >
                {resource.title}
              </Typography>

              {/* Description */}
              {resource.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    mb: 3,
                    lineHeight: 1.6
                  }}
                >
                  {resource.description}
                </Typography>
              )}

              {/* CTA Button */}
              <Button
                variant="contained"
                href={resource.url}
                target={resource.openInNewTab ? '_blank' : undefined}
                rel={resource.openInNewTab ? 'noopener noreferrer' : undefined}
                endIcon={resource.openInNewTab ? <OpenInNewIcon /> : null}
                sx={{
                  bgcolor: accentColor,
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: primaryColor
                  }
                }}
              >
                {resource.ctaText}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ py: 8, px: 3, bgcolor: '#f9f9f9' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: primaryColor,
              mb: 2
            }}
          >
            {heading}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Compact Layout - Icons Only */}
        {layout === 'compact' && socialLinks.length > 0 && (
          <SocialLinksSection showLabels={false} />
        )}

        {/* Cards Layout - Resource Links Only */}
        {layout === 'cards' && resourceLinks.length > 0 && (
          <ResourceLinksSection />
        )}

        {/* Hybrid Layout - Both Sections */}
        {layout === 'hybrid' && (
          <>
            {socialLinks.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: primaryColor,
                    textAlign: 'center',
                    mb: 4
                  }}
                >
                  Follow Us
                </Typography>
                <SocialLinksSection showLabels={true} />
              </Box>
            )}

            {resourceLinks.length > 0 && (
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: primaryColor,
                    textAlign: 'center',
                    mb: 4
                  }}
                >
                  Quick Links
                </Typography>
                <ResourceLinksSection />
              </Box>
            )}
          </>
        )}

        {/* Contact CTA Section */}
        {showContactCta && contactCtaHeading && (
          <Box
            sx={{
              mt: 8,
              p: 6,
              bgcolor: primaryColor,
              borderRadius: 3,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#fff',
                mb: 2
              }}
            >
              {contactCtaHeading}
            </Typography>
            {contactCtaDescription && (
              <Typography
                variant="body1"
                sx={{
                  color: '#fff',
                  opacity: 0.9,
                  mb: 4,
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                {contactCtaDescription}
              </Typography>
            )}
            {contactCtaButtonText && contactCtaButtonLink && (
              <Button
                variant="contained"
                href={contactCtaButtonLink}
                size="large"
                sx={{
                  bgcolor: accentColor,
                  color: '#fff',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#fff',
                    color: primaryColor
                  }
                }}
              >
                {contactCtaButtonText}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LinksBlock;